import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
  Box,
  IconButton,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import dayjs, {Dayjs} from 'dayjs';
import {useCallback, useMemo, useState} from 'react';
import {daysOfWeek} from '../../../dto/DateTime.dto';
import {DatePicker} from '@mui/x-date-pickers';
import {SeasonalDateRangeSelector} from './SeasonalDateRangeSelector';
import {ISeasonalDateRange} from './SeasonalDateRangeInput';
import {useCalculateAnnualDays} from './useCalculateAnnualDays';

export const NeptuneRadioEventOptions = ({
  selectedSeasonYear,
  setSelectedSeasonYear,
  totalDays,
  setTotalDays,
  totalYears,
  setTotalYears,
  daysOfOperation = [],
  setDaysOfOperation,
  holidayDates = [],
  setHolidayDates,
  seasonalDateRanges = [],
  setSeasonalDateRanges,
  seasonalMode = 'one-day',
  setSeasonalMode,
}: {
  selectedSeasonYear: string;
  setSelectedSeasonYear: (val: string) => void;
  totalDays: number;
  setTotalDays: (val: number) => void;
  totalYears: number;
  setTotalYears: (val: number) => void;
  daysOfOperation?: string[];
  setDaysOfOperation?: (val: string[] | ((prev: string[]) => string[])) => void;
  holidayDates?: (Dayjs | null)[];
  setHolidayDates?: (
    val: (Dayjs | null)[] | ((prev: (Dayjs | null)[]) => (Dayjs | null)[]),
  ) => void;
  seasonalDateRanges?: ISeasonalDateRange[];
  setSeasonalDateRanges?: React.Dispatch<
    React.SetStateAction<ISeasonalDateRange[]>
  >;
  seasonalMode?: 'one-day' | 'multiple-days';
  setSeasonalMode?: (val: 'one-day' | 'multiple-days') => void;
}) => {
  const handleLabelChange = useCallback(
    (type: string, value: string) => {
      if (type === 'totalDays' && Number(value) > 0) {
        setTotalDays(Number(value));
      } else if (type === 'totalYears' && Number(value) > 0) {
        setTotalYears(Number(value));
      }
    },
    [setTotalDays, setTotalYears],
  );

  const handleDayToggle = useCallback(
    (day: string) => {
      if (!setDaysOfOperation) return;

      setDaysOfOperation(prev => {
        if (prev.includes(day)) {
          return prev.filter(d => d !== day);
        }
        return [...prev, day];
      });
    },
    [setDaysOfOperation],
  );

  const handleSelectAllDays = useCallback(() => {
    if (!setDaysOfOperation) return;

    if (daysOfOperation.length === daysOfWeek.length) {
      setDaysOfOperation([]);
    } else {
      setDaysOfOperation([...daysOfWeek]);
    }
  }, [daysOfOperation, setDaysOfOperation]);

  const [newHolidayDate, setNewHolidayDate] = useState<Dayjs | null>(null);
  const [holidayDateError, setHolidayDateError] = useState<string>('');

  const handleHolidayDateChange = useCallback((date: Dayjs | null) => {
    setNewHolidayDate(date);
    setHolidayDateError('');
  }, []);

  const handleAddHoliday = useCallback(() => {
    if (!setHolidayDates || !newHolidayDate) return;

    // Check if date already exists
    const dateExists = holidayDates.some(
      existingDate =>
        existingDate &&
        existingDate.format('YYYY-MM-DD') ===
          newHolidayDate.format('YYYY-MM-DD'),
    );

    if (dateExists) {
      setHolidayDateError(
        `${newHolidayDate.format('MMMM DD, YYYY')} already exists`,
      );
      return;
    }

    setHolidayDates(prev => [...prev, newHolidayDate]);
    setNewHolidayDate(null);
    setHolidayDateError('');
  }, [setHolidayDates, newHolidayDate, holidayDates]);

  const handleDeleteHoliday = useCallback(
    (index: number) => {
      if (!setHolidayDates) return;
      setHolidayDates(prev => prev.filter((_, i) => i !== index));
    },
    [setHolidayDates],
  );

  // Calculate total days automatically for "All Year" and "Seasonally" options
  useCalculateAnnualDays(
    selectedSeasonYear,
    daysOfOperation,
    holidayDates,
    setTotalDays,
    seasonalDateRanges,
    seasonalMode,
  );

  // Restrict holiday dates to current year only
  const currentYearMinDate = useMemo(() => dayjs().startOf('year'), []);
  const currentYearMaxDate = useMemo(() => dayjs().endOf('year'), []);

  return (
    <>
      <Typography className='font-bold text-16 mb-2'>
        Are you open all Year or Seasonally?
      </Typography>
      <Stack direction={'row'} className='mb-5'>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedSeasonYear === 'All Year'}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedSeasonYear('All Year');
                } else {
                  setSelectedSeasonYear('');
                }
              }}
            />
          }
          label={<Typography>All Year</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedSeasonYear === 'Seasonally'}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedSeasonYear('Seasonally');
                } else {
                  setSelectedSeasonYear('');
                }
              }}
            />
          }
          label={<Typography>Seasonally</Typography>}
        />
      </Stack>

      {selectedSeasonYear === 'All Year' && (
        <Grid container spacing={3} className='mb-5'>
          <Grid item xs={12} md={6}>
            <Typography className='font-bold text-14 mb-2'>
              Select Days of Operation
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={daysOfOperation.length === daysOfWeek.length}
                    onChange={handleSelectAllDays}
                    disabled={!setDaysOfOperation}
                  />
                }
                label={
                  <Typography className='font-bold'>Select All</Typography>
                }
              />
              {daysOfWeek.map(day => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={daysOfOperation.includes(day)}
                      onChange={() => handleDayToggle(day)}
                      disabled={!setDaysOfOperation}
                    />
                  }
                  label={<Typography>{day}</Typography>}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              {holidayDateError && (
                <FormHelperText error>{holidayDateError}</FormHelperText>
              )}
            </Box>
            <Typography className='font-bold text-14 mb-2'>Holidays</Typography>
            <Box>
              <Stack className='flex-row gap-2 items-center mb-3'>
                <Box>
                  <DatePicker
                    label='Select Holiday Date'
                    value={newHolidayDate}
                    onChange={handleHolidayDateChange}
                    minDate={currentYearMinDate}
                    maxDate={currentYearMaxDate}
                    slotProps={{
                      textField: {
                        error: !!holidayDateError,
                        InputLabelProps: {
                          shrink: true,
                          className: 'inputLabel-shink-background',
                        },
                      },
                      popper: {'aria-label': 'Holiday date dialog'},
                    }}
                  />
                </Box>
                <Button
                  variant='contained'
                  size='medium'
                  startIcon={<AddIcon />}
                  onClick={handleAddHoliday}
                  disabled={!setHolidayDates || !newHolidayDate}>
                  Add
                </Button>
              </Stack>
            </Box>

            {holidayDates.length > 0 && (
              <Box className='max-h-300 overflow-auto scrollbar'>
                <List dense>
                  {holidayDates.map((date, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge='end'
                          aria-label='delete'
                          onClick={() => handleDeleteHoliday(index)}
                          color='error'
                          size='small'>
                          <DeleteIcon />
                        </IconButton>
                      }>
                      <ListItemText
                        primary={
                          date ? date.format('MMMM DD, YYYY') : 'Invalid Date'
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {selectedSeasonYear === 'Seasonally' && setSeasonalDateRanges && (
        <Box className='mb-5'>
          <SeasonalDateRangeSelector
            dateRanges={seasonalDateRanges}
            setDateRanges={setSeasonalDateRanges}
            dateType={seasonalMode}
            setDateType={setSeasonalMode}
          />
        </Box>
      )}
      <Typography className='font-bold text-16 mt-3 mb-2'>
        How many TOTAL days are you open annually?
      </Typography>
      <TextField
        label='Total Days'
        placeholder='Enter number'
        type='number'
        size='small'
        value={totalDays}
        onChange={e => handleLabelChange('totalDays', e.target.value)}
        disabled={
          selectedSeasonYear === 'All Year' ||
          selectedSeasonYear === 'Seasonally'
        }
        helperText={
          selectedSeasonYear === 'All Year'
            ? 'Auto-calculated based on days of operation and holidays'
            : selectedSeasonYear === 'Seasonally'
            ? 'Auto-calculated based on seasonal date ranges'
            : ''
        }
      />
      <Typography className='font-bold text-16 mt-3 mb-2'>
        What is your estimated total yearly attendance?{' '}
      </Typography>
      <TextField
        label='Total Yearly'
        placeholder='Enter number'
        type='number'
        size='small'
        value={totalYears}
        onChange={e => handleLabelChange('totalYears', e.target.value)}
      />
    </>
  );
};
